import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { useGetGeneFactsOverviewQuery } from '../../api/variantApi';
import LoadMoreBtn from '../components/LoadMoreBtn';
import GeneEditReportTextModal from './GeneEditReportTextModal';

type GeneFactsOverviewSectionProps = {
  forceExpanded?: boolean;
};

const GeneFactsOverviewSection = ({
  forceExpanded = false,
}: GeneFactsOverviewSectionProps) => {
  const { vid } = useParams();
  const { data: geneFactsOverview } = useGetGeneFactsOverviewQuery(
    { variant_id: vid },
    { skip: !vid },
  );
  const [isGeneModalOpen, setIsGeneModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isSectionExpanded = forceExpanded || isExpanded;

  const handleOpenGeneModal = () => setIsGeneModalOpen(true);
  const handleCloseGeneModal = () => setIsGeneModalOpen(false);
  const handleSaved = async () => {
    // RTK Query will automatically refetch via tag invalidation
    // The mutation's invalidatesTags marks GeneFactsOverview stale, so the  table refetches automatically
  };

  const columns = [
    {
      title: 'Gene',
      dataIndex: 'hgnc_symbol',
      key: 'hgnc_symbol',
      render: (_: string, record: any) => (
        <span
          className="clinical-obs__report-category-link"
          onClick={(e) => {
            e.preventDefault();
            handleOpenGeneModal();
          }}
        >
          {record.hgnc_symbol}
        </span>
      ),
    },
    {
      title: 'Protein Name',
      dataIndex: 'protein_name',
      key: 'protein_name',
    },
    {
      title: 'OMIMs',
      dataIndex: 'omims',
      key: 'omims',
      render: (_: any, record: any) =>
        Array.isArray(record.omims) && record.omims.length > 0 ? (
          <span>
            {record.omims.map((omim: any, idx: number) => (
              <span key={omim.omim}>
                <a
                  href={omim.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gene-facts-ncbi-link__anchor"
                >
                  {omim.omim}
                </a>
                {idx < record.omims.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        ) : null,
    },
    {
      title: 'Cytogenetic Location',
      dataIndex: 'cytogenetic_location',
      key: 'cytogenetic_location',
    },
    {
      title: 'Protein name (for report text)',
      dataIndex: 'report_text_protein',
      key: 'report_text_protein',
    },
    {
      title: 'Function (for report text)',
      dataIndex: 'report_text_function',
      key: 'report_text_function',
    },
    {
      title: 'Phenotypes (for report text)',
      dataIndex: 'report_text_disorders',
      key: 'report_text_disorders',
      render: (text: string) => (
        <div
          style={{ whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text || '') }}
        />
      ),
    },
  ];

  const biologicalColumns = [
    {
      title: 'Biological processes',
      dataIndex: 'biological_processes',
      key: 'biological_processes',
    },
    {
      title: 'Molecular function',
      dataIndex: 'molecular_functions',
      key: 'molecular_functions',
    },
    {
      title: 'Physiological function',
      dataIndex: 'physiological_function',
      key: 'physiological_function',
    },
  ];

  const phenotypeColumns = [
    {
      title: 'Preferred Term (EN)',
      dataIndex: 'preferred_term_en',
      key: 'preferred_term_en',
      width: 200,
      render: (text: string) => <b>{text}</b>,
    },
    {
      title: 'Inheritance',
      dataIndex: 'inheritance',
      key: 'inheritance',
      width: 150,
    },
    {
      title: 'OMIM ID',
      dataIndex: 'omim_id',
      key: 'omim_id',
      width: 150,
      render: (text: string, record: any) => (
        <a
          href={record.omim_url}
          target="_blank"
          rel="noopener noreferrer"
          className="gene-facts-ncbi-link__anchor"
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Orpha ID',
      dataIndex: 'orpha_id',
      key: 'orpha_id',
      width: 150,
      render: (text: string, record: any) => (
        <a
          href={record.orpha_url}
          target="_blank"
          rel="noopener noreferrer"
          className="gene-facts-ncbi-link__anchor"
        >
          {text}
        </a>
      ),
    },
  ];

  const geneChromosomeColumns = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 200,
      render: (text: string) => <b>{text}</b>,
    },
    {
      title: 'Reference Genome',
      dataIndex: 'refgenome',
      key: 'refgenome',
      width: 150,
    },
    { title: 'Start', dataIndex: 'start', key: 'start', width: 150 },
    { title: 'End', dataIndex: 'end', key: 'end', width: 150 },
  ];

  if (!geneFactsOverview) {
    return null;
  }

  return (
    <div className="gene-facts-overview-section">
      {isGeneModalOpen && geneFactsOverview?.id != null && (
        <GeneEditReportTextModal
          geneId={geneFactsOverview.id}
          onClose={handleCloseGeneModal}
          onSaved={handleSaved}
          stageType="interpretation"
        />
      )}
      <div className="gene-facts-ncbi-link">
        <a
          href={geneFactsOverview.ncbi_gene_reviews_url}
          target="_blank"
          rel="noopener noreferrer"
          className="gene-facts-ncbi-link__anchor"
        >
          NCBI
        </a>
        <a
          href={geneFactsOverview.hgnc_url}
          target="_blank"
          rel="noopener noreferrer"
          className="gene-facts-ncbi-link__anchor"
        >
          HGNC
        </a>
        {geneFactsOverview.clinicalgenome_gene_url && (
          <a
            href={geneFactsOverview.clinicalgenome_gene_url}
            target="_blank"
            rel="noopener noreferrer"
            className="gene-facts-ncbi-link__anchor"
          >
            ClinGen
          </a>
        )}
        {geneFactsOverview.gencc_gene_url && (
          <a
            href={geneFactsOverview.gencc_gene_url}
            target="_blank"
            rel="noopener noreferrer"
            className="gene-facts-ncbi-link__anchor"
          >
            GenCC
          </a>
        )}
        {geneFactsOverview.cspec_url && (
          <a
            href={geneFactsOverview.cspec_url}
            target="_blank"
            rel="noopener noreferrer"
            className="gene-facts-ncbi-link__anchor"
          >
            CSpec
          </a>
        )}
      </div>
      <ResizableTable
        dataSource={[geneFactsOverview]}
        columns={columns}
        rowKey="id"
        pagination={false}
        persistenceKey="interpretation-gene-facts"
      />
      <div className="gene-facts-overview__expandable">
        <div className="gene-facts-overview__expandable-inner">
          {isSectionExpanded && (
            <div style={{ marginTop: 8, marginLeft: 24 }}>
              <ResizableTable
                dataSource={[geneFactsOverview]}
                columns={biologicalColumns}
                rowKey="id"
                pagination={false}
                size="small"
                persistenceKey="interpretation-gene-facts-biological"
              />
            </div>
          )}
          <LoadMoreBtn
            showMore={isSectionExpanded}
            handleShowMoreClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      </div>
      <div className="gene-facts-section-spacer" />
      <h3 className="gene-facts-section-title">Phenotypes</h3>
      <ResizableTable
        dataSource={geneFactsOverview.phenotypes}
        columns={phenotypeColumns}
        rowKey="preferred_term_en"
        pagination={false}
        persistenceKey="interpretation-gene-facts-phenotypes"
      />
      <div className="gene-facts-section-spacer" />
      <h3 className="gene-facts-section-title">Gene Chromosomes</h3>
      <ResizableTable
        dataSource={geneFactsOverview.gene_chromosomes}
        columns={geneChromosomeColumns}
        rowKey="symbol"
        pagination={false}
        persistenceKey="interpretation-gene-chromosomes"
      />
    </div>
  );
};

export default GeneFactsOverviewSection;
